import type { AxiosError, InternalAxiosRequestConfig } from 'axios'
import axios from 'axios'
import { storeToRefs } from 'pinia'
import { useCheckServerStatus } from '@/composables/use-check-server-status'
import { useGlobalMessage } from '@/composables/use-global-message'
import { useRefreshToken } from '@/composables/use-refresh-token'
import { RouterName } from '@/enums/router-name'
import i18n from '@/locales'
import { piniaInstance } from '@/stores/pinia-instance'
import { useUserBindingStore } from '@/stores/use-user-binding-store'
import { LocaleToApi, RtnCode } from '~library/enums'
import {
  ApiFactory,
  getBaseUrl,
  type ApiRequestHeaders,
  type ApiResponse
} from '~library/services'
import LoginApi from './apis/login-api'
import axiosUnAuthInstance from './axios-un-auth-instance'

interface ErrorConfig {
  error: AxiosError
  config: InternalAxiosRequestConfig
  resolve: (value: unknown) => void
}

let retryList: ErrorConfig[] = []
let isUpdatingStatus = false

const axiosInstance = axios.create({
  baseURL: getBaseUrl()
})

const loginApi = new LoginApi(new ApiFactory(axiosUnAuthInstance))

const takeNewToken = async (): Promise<string | null> => {
  const { userBindingData } = storeToRefs(useUserBindingStore())
  const { setUserBindingData } = useUserBindingStore()

  try {
    isUpdatingStatus = true

    const tokenData = await useRefreshToken(loginApi).refreshToken()

    if (!userBindingData.value || !tokenData) {
      return null
    }

    setUserBindingData({
      ...userBindingData.value,
      ...tokenData
    })
    return tokenData.token
  } catch {
    // 呼叫 API refreshToken 錯誤時，返回 null 觸發登出
    return null
  } finally {
    isUpdatingStatus = false
  }
}

const retryApiPendingWithNewToken = async (token: string) => {
  for (const request of retryList) {
    request.config.headers.Authorization = `Bearer ${token}`

    request.config.headers['X-Token-Refreshed'] = 'true'

    const data = await axiosInstance(request.config)
    request.resolve(data)
  }

  retryList = []
}

axiosInstance.interceptors.request.use(
  async (config) => {
    const { userBindingData } = storeToRefs(useUserBindingStore(piniaInstance))
    const headers: ApiRequestHeaders = config.headers as ApiRequestHeaders

    headers.lang = LocaleToApi[i18n.global.locale.value]

    const userProfile = userBindingData.value?.userProfile

    if (userProfile) {
      headers.Authorization = `Bearer ${userProfile.token}`
      headers.bizId = userProfile.bizId
      headers.memberCardNumber = userProfile.memberCardNumber

      if (userProfile.identity) {
        headers.identity = userProfile.identity
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

axiosInstance.interceptors.response.use(
  (response: ApiResponse) => {
    /** 只要 rtnCode 不為 0 和 96，都跳訊息 */

    if (
      response.data.rtnCode &&
      ![0, 96].includes(response.data.rtnCode) &&
      response.data.message
    ) {
      useGlobalMessage().showApiErrorMessage(response)
    }

    if (
      response.data.rtnCode === RtnCode.TOKEN_EXPIRED ||
      response.data.rtnCode === RtnCode.LATCHKEY_CHANGED ||
      response.data.rtnCode === RtnCode.ACCESS_PERMISSIONS_MODIFIED
    ) {
      logout()
    }

    return response
  },

  async (error: AxiosError) => {
    const { userBindingData } = storeToRefs(useUserBindingStore(piniaInstance))
    const { checkServerStatus } = useCheckServerStatus()
    const { config, response } = error
    const token = userBindingData.value?.userProfile?.token

    if (!(await checkServerStatus(error))) {
      return
    }

    if (response?.status !== 401) {
      useGlobalMessage().showApiErrorMessage(error.response as ApiResponse)
    }

    if (response?.status === 401 && config) {
      // 如果已經重試過 token refresh 仍然 401，直接登出避免無窮循環
      if (config.headers['X-Token-Refreshed'] === 'true') {
        logout()
        return Promise.reject(error)
      }

      if (!isUpdatingStatus) {
        const api = new Promise((resolve) => {
          retryList.push({
            error,
            config,
            resolve
          })
        })

        /** !isUpdating 確保一次只進來一個 */
        /** 取 new token */
        const newToken = await takeNewToken()

        if (newToken) {
          /** 重打 api */
          retryApiPendingWithNewToken(newToken)
          return api
        } else {
          retryList.forEach(async (request) => {
            request.resolve(error.response)
          })
          logout()
        }
      } else if (
        /** 儲存 error config */
        config.headers.Authorization === `Bearer ${token}`
      ) {
        return await new Promise((resolve) => {
          retryList.push({
            error,
            config,
            resolve
          })
        })
      } else if (token) {
        await axiosInstance(config)
      }
    }

    return Promise.reject(error)
  }
)

async function logout() {
  /** 清空使用者綁定資料 */
  useUserBindingStore(piniaInstance).setUserBindingData(null)

  const router = (await import('@/router')).default
  router.push({ name: RouterName.COMPANY_EMPLOYEE })
}

export default axiosInstance
